<script setup lang="ts">
  import { computed, ref } from 'vue';
  import type { DestinationPrices } from '@/entities/searchCriteria';
  import type { SearchFormGoal, SearchFormTracking } from '@/entities/searchForm';
  import type { AccountUser } from '@/features/account';
  import { SerpLayout, useSerpCriteria } from '@/layouts/serp';
  import { STAY_CALENDAR_AB_TEST } from '@/shared/config/abTests';
  import { CountryResortsUrl, FormDataUrl, IntelligentSearchUrl } from '@/shared/config/formData';
  import { abTests } from '@/shared/lib/AbTests';
  import { SearchForm, type SearchFormCriteria, type SearchFormExpose } from '@/widgets/searchForm';

  // Повторяет SerpPageProps из layouts/serp: compiler-sfc Vue 2.7 не резолвит импортированный
  // тип в defineProps, поэтому контракт страницы объявлен здесь.
  interface HotelsSearchProps {
    modelValue?: SearchFormCriteria | null;
    user?: AccountUser | null;
    accountUrl?: string;
    prices?: DestinationPrices;
    departureDisabled?: boolean;
    destinationDisabled?: boolean;
  }

  const props = withDefaults(defineProps<HotelsSearchProps>(), {
    modelValue: null,
  });

  interface HotelsSearchEmits {
    (e: 'update:modelValue', criteria: SearchFormCriteria): void;
    (e: 'search', criteria: SearchFormCriteria): void;
    (e: 'track', goal: SearchFormGoal): void;
    (e: 'login'): void;
    (e: 'logout'): void;
    (e: 'buy'): void;
  }
  const emit = defineEmits<HotelsSearchEmits>();

  const TRACKING: SearchFormTracking = { pageType: 'serp', productType: 'hotel' };

  const searchFormRef = ref<SearchFormExpose | null>(null);
  const isStayCalendar = computed(() => abTests.hasTest(STAY_CALENDAR_AB_TEST));
  const criteria = useSerpCriteria(props, emit, isStayCalendar);

  defineExpose<SearchFormExpose>({
    validate: () => (searchFormRef.value ? searchFormRef.value.validate() : true),
    submit: () => searchFormRef.value?.submit(),
    reset: () => searchFormRef.value?.reset(),
  });
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <SerpLayout
    :user="user"
    :account-url="accountUrl"
    @login="emit('login')"
    @logout="emit('logout')"
    @buy="emit('buy')"
  >
    <template #form="{ collapsed }">
      <SearchForm
        ref="searchFormRef"
        v-model="criteria"
        :rounded-bottom="collapsed"
        :prices="prices"
        :is-visible-departure="false"
        date-field-label="Дата заезда"
        tourists-field-label="Гости"
        nights-summary-prefix="С"
        search-btn-text="Найти отели"
        :is-visible-room-button="false"
        :departure-disabled="departureDisabled"
        :destination-disabled="destinationDisabled"
        :form-url="FormDataUrl.Hotels"
        :search-url="IntelligentSearchUrl.Hotels"
        :resorts-url="CountryResortsUrl.Hotels"
        :is-stay-calendar="isStayCalendar"
        :tracking="TRACKING"
        @search="emit('search', $event)"
        @track="emit('track', $event)"
      />
    </template>
  </SerpLayout>
</template>
